import React, { useEffect, useState } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogHeader,
  Box,
  Button,
  ButtonText,
  CloseIcon,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import type { ModuleUpgradeOffer } from '@/common/api/models/moduleUpgradeOffer';
import { formatModuleVersionLabel, getUpgradeOfferTitleKey } from './moduleUpgradeOfferUtils';
import { ModuleUpgradeOfferModal } from './ModuleUpgradeOfferModal';

interface ModuleUpgradeOfferBannerProps {
  courseId: string;
  moduleId: string;
  upgradeOffer: ModuleUpgradeOffer;
  onUpgraded?: () => void;
  onDismissed?: () => void;
}

/**
 * Compact ribbon on the module screen when a newer version is offered.
 */
export const ModuleUpgradeOfferBanner: React.FC<ModuleUpgradeOfferBannerProps> = ({
  courseId,
  moduleId,
  upgradeOffer,
  onUpgraded,
  onDismissed,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const isFocused = useIsFocused();

  useEffect(() => {
    if (isFocused && !upgradeOffer.autoPromptSuppressed) {
      setModalOpen(true);
    }
  }, [isFocused, upgradeOffer.autoPromptSuppressed]);

  const versionLabel = t('student.module.upgrade-offer.version-label', {
    from: formatModuleVersionLabel(upgradeOffer.pinnedVersion),
    to: formatModuleVersionLabel(upgradeOffer.targetVersion),
  });
  const modalTitleKey = getUpgradeOfferTitleKey(upgradeOffer.changeSummary);
  const summaryTitle = t(modalTitleKey);
  const trimmedHeadline = upgradeOffer.headline?.trim() ?? '';
  const isGenericSummaryTitle = modalTitleKey === 'student.module.upgrade-offer.modal-title';
  const ribbonTitle = isGenericSummaryTitle && trimmedHeadline
    ? trimmedHeadline
    : summaryTitle;
  const showGradeSubline = upgradeOffer.changeSummary?.gradeBecomesOralOnly ?? false;
  const gradeSubline = showGradeSubline
    ? t('student.module.upgrade-offer.ribbon-subline-oral-only', {
      version: upgradeOffer.targetVersion,
    })
    : null;

  return (
    <>
      <Box
        testID="module-upgrade-offer-ribbon"
        bg="$warning100"
        borderLeftWidth={3}
        borderLeftColor="$warning500"
        px="$3"
        py="$2"
        mb="$2"
      >
        <HStack alignItems="center" justifyContent="space-between" space="sm">
          <VStack flex={1} space="xs">
            <Text
              size="sm"
              fontWeight="$bold"
              color="$warning800"
              testID="module-upgrade-offer-ribbon-title"
            >
              {ribbonTitle}
            </Text>
            {gradeSubline ? (
              <Text
                size="sm"
                color="$warning800"
                numberOfLines={2}
                testID="module-upgrade-offer-ribbon-subline"
              >
                {gradeSubline}
              </Text>
            ) : null}
          </VStack>
          <Button
            size="xs"
            variant="outline"
            onPress={() => setModalOpen(true)}
            testID="module-upgrade-offer-review-btn"
          >
            <ButtonText>
              {t('student.module.upgrade-offer.review')}
            </ButtonText>
          </Button>
        </HStack>
      </Box>
      {modalOpen ? (
        <AlertDialog isOpen onClose={() => setModalOpen(false)} size="sm">
          <AlertDialogBackdrop />
          <AlertDialogContent bg="$bgCard" w="95%" maxWidth={420} maxHeight="90%">
            <AlertDialogHeader borderBottomWidth="$0" alignItems="flex-start">
              <VStack flex={1} pr="$2">
                <Heading size="lg" testID="module-upgrade-offer-modal-title">
                  {t(modalTitleKey)}
                </Heading>
                <Text size="sm" color="$textSubtle">
                  {versionLabel}
                </Text>
              </VStack>
              <AlertDialogCloseButton testID="module-upgrade-offer-close-btn">
                <Icon as={CloseIcon} mr="$4" mb="$2" />
              </AlertDialogCloseButton>
            </AlertDialogHeader>
            <ModuleUpgradeOfferModal
              courseId={courseId}
              moduleId={moduleId}
              upgradeOffer={upgradeOffer}
              onClose={() => setModalOpen(false)}
              onSuccess={onUpgraded}
              onDismissed={onDismissed}
            />
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </>
  );
};

export default ModuleUpgradeOfferBanner;
